import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import Image, { type StaticImageData } from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';
import UniversityBadge from '../UniversityBadge';
import styles from './ProfessorCard.module.scss';

/**
 * `stacked`: the image above the name, detail and badge (the redesign's
 * DegreeDetail grid; the legacy program cards). `overlay`: the image fills
 * the card and the copy sits over a dark scrim at its foot (the paralegal
 * page's cards).
 */
export type ProfessorCardLayout = 'stacked' | 'overlay';

/**
 * The name's element: what the page renders today (h6 on the program pages,
 * strong on the paralegal page, h2 on /professors).
 */
export type ProfessorCardNameElement = 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'strong';

/**
 * The detail line's element: a paragraph, or the h4 /professors renders its
 * "also teaches at:" line as today (Phase 5 PR 5.4: heading levels do not
 * change). The look is the same either way.
 */
export type ProfessorCardDetailElement = 'p' | 'h4';
export interface ProfessorCardImage {
  src: StaticImageData | string;
  /**
   * Shown instead of `src` up to 991px, below `lg` (the legacy
   * `desktop-hidden` / `tablet-hidden` pair switched at 992px): a crop for the
   * one-column layout. Same `alt`.
   */
  mobileSrc?: StaticImageData | string;
  alt: string;
  /** Required: the rendered width per breakpoint, so next/image picks the right candidate. */
  sizes: string;
}
export interface ProfessorCardProps {
  name: string;
  /** Defaults to `h3`. */
  nameAs?: ProfessorCardNameElement;
  /** When given, the whole card is one link; nothing inside it is interactive. */
  href?: string;
  /**
   * `_blank` opens the link in a new tab, as some legacy cards do. Forwarded
   * to the link only (with `href`); no `rel` is added for it.
   */
  target?: '_blank';
  /** Forwarded verbatim to the link (with `href`). None by default. */
  rel?: string;
  /** No `priority`: a professor card is never a page's LCP image. */
  image: ProfessorCardImage;
  /** One line under the name, such as the qualification or subject. */
  detail?: ReactNode;
  /** The detail line's element. Defaults to `p`. */
  detailAs?: ProfessorCardDetailElement;
  /** Rendered as a UniversityBadge. */
  affiliation?: string;
  layout?: ProfessorCardLayout;
}

// The art-directed source's URL: a static import's file, as Media does (the
// browser fetches it unoptimised, like the legacy `desktop-hidden` copy).
function staticSrc(src: StaticImageData | string) {
  return typeof src === 'string' ? src : src.src;
}

/**
 * A professor's card (Phase 5 PR 5.0, incubating). The image, the name, an
 * optional detail line and an optional university badge, in the `stacked` or
 * `overlay` layout; with `href` the card is one Next link (with `target` and
 * `rel` when given). With `image.mobileSrc` the photo is a `<picture>` that
 * switches below `lg`. Presentational: no analytics, no `className`, no
 * `style`.
 */

function ProfessorCard(t0) {
  const $ = _c(36);
  const {
    name,
    nameAs: t1,
    href,
    target,
    rel,
    image,
    detail,
    detailAs: t2,
    affiliation,
    layout: t3
  } = t0;
  const Name = t1 === undefined ? "h3" : t1;
  const Detail = t2 === undefined ? "p" : t2;
  const layout = t3 === undefined ? "stacked" : t3;
  let t4;
  if ($[0] !== image.alt || $[1] !== image.sizes || $[2] !== image.src) {
    t4 = <Image alt={image.alt} className={styles["helios-professor-card__image"]} fill={true} sizes={image.sizes} src={image.src} />;
    $[0] = image.alt;
    $[1] = image.sizes;
    $[2] = image.src;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const photo = t4;
  let t5;
  if ($[4] !== image.mobileSrc || $[5] !== photo) {
    t5 = <div className={styles["helios-professor-card__frame"]}>{image.mobileSrc ? <picture className={styles["helios-professor-card__picture"]}><source media="(max-width: 991px)" srcSet={staticSrc(image.mobileSrc)} />{photo}</picture> : photo}</div>;
    $[4] = image.mobileSrc;
    $[5] = photo;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== Name || $[8] !== name) {
    t6 = <Name className={styles["helios-professor-card__name"]}>{name}</Name>;
    $[7] = Name;
    $[8] = name;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== Detail || $[11] !== detail) {
    t7 = detail ? <Detail className={styles["helios-professor-card__detail"]}>{detail}</Detail> : null;
    $[10] = Detail;
    $[11] = detail;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== affiliation) {
    t8 = affiliation ? <p className={styles["helios-professor-card__affiliation"]}><UniversityBadge name={affiliation} /></p> : null;
    $[13] = affiliation;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6 || $[16] !== t7 || $[17] !== t8) {
    t9 = <div className={styles["helios-professor-card__copy"]}>{t6}{t7}{t8}</div>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t5 || $[20] !== t9) {
    t10 = <>{t5}{t9}</>;
    $[19] = t5;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const body = t10;
  const t11 = href && styles["helios-professor-card__body--link"];
  let t12;
  if ($[22] !== t11) {
    t12 = classNames(styles["helios-professor-card__body"], t11);
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const bodyClass = t12;
  const t13 = styles[`helios-professor-card--${layout}`];
  let t14;
  if ($[24] !== t13) {
    t14 = classNames(styles["helios-professor-card"], t13);
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== body || $[27] !== bodyClass || $[28] !== href || $[29] !== rel || $[30] !== target) {
    t15 = href ? <Link className={bodyClass} href={href} rel={rel} target={target}>{body}</Link> : <div className={bodyClass}>{body}</div>;
    $[26] = body;
    $[27] = bodyClass;
    $[28] = href;
    $[29] = rel;
    $[30] = target;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== layout || $[33] !== t14 || $[34] !== t15) {
    t16 = <article className={t14} data-helios-professor-card="" data-layout={layout}>{t15}</article>;
    $[32] = layout;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
}
export default ProfessorCard;